import React, { useCallback, useEffect, useState } from 'react';
import {
  Store, Handshake, Siren, Loader2, CheckCircle2, XCircle, Trash2,
  UserPlus, Plus, Layers, Send, RefreshCw, Search,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SkeletonRows, EmptyState } from './OrgPanelShared';

const TIER_STYLES = {
  trusted: 'bg-emerald-100 text-emerald-700',
  preferred: 'bg-indigo-100 text-indigo-700',
  standard: 'bg-slate-100 text-slate-600',
};

// ============================ MARKETPLACE ============================

export function MarketplaceTab() {
  const [board, setBoard] = useState('mine');
  const [shifts, setShifts] = useState(null);
  const [plan, setPlan] = useState(null); // {job, plan}
  const [busy, setBusy] = useState('');

  const load = useCallback(async (b) => {
    setShifts(null);
    try {
      const r = await apiCall(`/api/marketplace/shifts?board=${b}`);
      setShifts(r.shifts || []);
    } catch (e) { toast.error(e.message); setShifts([]); }
  }, []);
  useEffect(() => { load(board); }, [board, load]);

  const openPlan = async (job) => {
    setBusy(`plan-${job._id}`);
    try {
      const r = await apiCall(`/api/org/shifts/${job._id}/exposure-plan`);
      setPlan({ job, ...r.plan });
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const assignBest = async () => {
    setBusy('act');
    try {
      await apiCall(`/api/org/shifts/${plan.job._id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ worker_id: String(plan.best_roster_candidate.worker_id), force: true }),
      });
      toast.success(`${plan.best_roster_candidate.name} assigned`);
      setPlan(null); load(board);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const publishEmergency = async () => {
    setBusy('act');
    try {
      const r = await apiCall(`/api/org/shifts/${plan.job._id}/publish-emergency`, { method: 'POST' });
      toast.success(`Published to emergency board — ${r.notified} top match(es) notified`);
      setPlan(null); load(board);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const requestPartner = async (partnershipId) => {
    setBusy('act');
    try {
      const w = await apiCall(`/api/org/partners/${partnershipId}/shared-workers`);
      const first = (w.workers || [])[0];
      if (!first) { toast.warning('Partner has no shared workers'); return; }
      await apiCall(`/api/org/shifts/${plan.job._id}/request-shared-worker`, {
        method: 'POST',
        body: JSON.stringify({ partnership_id: partnershipId, worker_id: first.worker_id }),
      });
      toast.success(`Request sent for ${first.name} — awaiting partner approval`);
      setPlan(null);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const BOARDS = [
    { id: 'mine', label: 'My Open Shifts', icon: Store },
    { id: 'emergency', label: 'Emergency', icon: Siren },
    { id: 'partner', label: 'Partner Shifts', icon: Handshake },
  ];
  return (
    <div className="space-y-3" data-testid="marketplace-tab">
      <div className="flex items-center gap-1.5 flex-wrap">
        {BOARDS.map((b) => {
          const Icon = b.icon;
          return (
            <button key={b.id} onClick={() => setBoard(b.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${board === b.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`market-board-${b.id}`}>
              <Icon className="w-3.5 h-3.5" /> {b.label}
            </button>
          );
        })}
        <button onClick={() => load(board)} className="ml-auto text-xs text-indigo-600 inline-flex items-center gap-1" data-testid="market-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      {shifts === null ? <SkeletonRows n={4} /> : !shifts.length ? (
        <EmptyState label={board === 'partner' ? 'No open shifts from partner organizations.' : board === 'emergency' ? 'No emergency shifts.' : 'No unfilled upcoming shifts.'} />
      ) : (
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl">
          {shifts.map((j) => (
            <li key={j._id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`market-shift-${j._id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {j.emergency && <span className="mr-1.5 px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-bold">EMERGENCY</span>}
                  {j.title}
                </p>
                <p className="text-xs text-slate-500">{j.start_time?.replace('T', ' ')} · ${j.pay_rate}/hr{j.org_name && board === 'partner' ? ` · ${j.org_name}` : ''}</p>
              </div>
              {board !== 'partner' && (
                <button onClick={() => openPlan(j)} disabled={busy === `plan-${j._id}`}
                  className="shrink-0 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50"
                  data-testid={`exposure-plan-btn-${j._id}`}>
                  {busy === `plan-${j._id}` ? 'Evaluating…' : 'Exposure Plan'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {plan && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPlan(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="exposure-plan-modal">
            <h4 className="text-sm font-bold text-slate-900">Open Shift Intelligence — {plan.job.title}</h4>
            <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50 p-3">
              <p className="text-xs font-bold text-violet-700 uppercase" data-testid="exposure-recommendation">{plan.recommendation.replace(/_/g, ' ')}</p>
              <p className="text-sm text-slate-700 mt-1">{plan.reason}</p>
            </div>
            <div className="mt-3 space-y-2 text-xs text-slate-600">
              <p><span className="font-semibold">1. Roster:</span> {plan.eligible_count} eligible{plan.best_roster_candidate ? ` — best: ${plan.best_roster_candidate.name} (${plan.best_roster_candidate.score})` : ''}</p>
              <p><span className="font-semibold">2. Float pools:</span> {plan.float_pool_candidates.length ? plan.float_pool_candidates.map((c) => `${c.name} (${c.pool})`).join(', ') : 'no available pool workers'}</p>
              <p><span className="font-semibold">3. Partners:</span> {plan.partner_options.length ? plan.partner_options.map((p) => `${p.partner_name} (${p.shared_worker_count} shared)`).join(', ') : 'no partner capacity'}</p>
              <p><span className="font-semibold">4. Marketplace:</span> {plan.urgent ? 'urgent — emergency board recommended' : 'open board available'}</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {plan.best_roster_candidate && (
                <button onClick={assignBest} disabled={busy === 'act'} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="exposure-assign-best-btn">
                  Assign {plan.best_roster_candidate.name}
                </button>
              )}
              {plan.partner_options.map((p) => (
                <button key={p.partnership_id} onClick={() => requestPartner(p.partnership_id)} disabled={busy === 'act'}
                  className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
                  data-testid={`exposure-request-partner-${p.partnership_id}`}>
                  Request from {p.partner_name}
                </button>
              ))}
              {!plan.job.emergency && (
                <button onClick={publishEmergency} disabled={busy === 'act'} className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50" data-testid="exposure-publish-emergency-btn">
                  Publish Emergency
                </button>
              )}
              <button onClick={() => setPlan(null)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium" data-testid="exposure-plan-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ SHARING & POOLS ============================

export function SharingPoolsTab() {
  const [settings, setSettings] = useState(null);
  const [partners, setPartners] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pools, setPools] = useState([]);
  const [roster, setRoster] = useState([]);
  const [busy, setBusy] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [discover, setDiscover] = useState(null);
  const [newPool, setNewPool] = useState({ name: '', type: 'employer' });

  const load = useCallback(async () => {
    try {
      const me = await apiCall('/api/auth/me');
      const role = me.user?.role || me.role;
      const [s, p, r, fp, dir] = await Promise.all([
        apiCall('/api/org/sharing-settings'),
        apiCall('/api/org/partners'),
        apiCall('/api/org/sharing-requests'),
        apiCall('/api/org/float-pools'),
        apiCall(role === 'agent' ? '/api/agent/workers' : '/api/worker/directory'),
      ]);
      setSettings(s.settings);
      setPartners(p.partnerships || []);
      setRequests(r.requests || []);
      setPools(fp.pools || []);
      const list = dir.workers || dir.data || [];
      setRoster(list.map((w) => ({ id: w.worker_id || w._id, name: w.name || w.full_name || w.worker_name || w.email })).filter((w) => w.id));
    } catch (e) { toast.error(e.message); setSettings({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadPools = useCallback(async () => {
    try {
      const fp = await apiCall('/api/org/float-pools');
      setPools(fp.pools || []);
    } catch (e) { toast.error(e.message); }
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        apiCall('/api/org/partners'),
        apiCall('/api/org/sharing-requests'),
      ]);
      setPartners(p.partnerships || []);
      setRequests(r.requests || []);
    } catch (e) { toast.error(e.message); }
  }, []);

  const saveSettings = async () => {
    setBusy('settings');
    try {
      const r = await apiCall('/api/org/sharing-settings', {
        method: 'PUT',
        body: JSON.stringify({ enabled: settings.enabled, region: settings.region || '' }),
      });
      setSettings(r.settings); toast.success('Sharing settings saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const invite = async (payload) => {
    setBusy('invite');
    try {
      await apiCall('/api/org/partners/invite', { method: 'POST', body: JSON.stringify(payload) });
      toast.success('Partnership invite sent'); setInviteEmail(''); setDiscover(null); loadPartners();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const respond = async (pid, action) => {
    try {
      await apiCall(`/api/org/partners/${pid}/respond`, { method: 'POST', body: JSON.stringify({ action }) });
      toast.success(`Invite ${action === 'accept' ? 'accepted' : 'declined'}`); loadPartners();
    } catch (e) { toast.error(e.message); }
  };

  const endPartner = async (pid) => {
    try {
      await apiCall(`/api/org/partners/${pid}`, { method: 'DELETE' });
      toast.success('Partnership ended'); loadPartners();
    } catch (e) { toast.error(e.message); }
  };

  const decide = async (rid, action) => {
    try {
      await apiCall(`/api/org/sharing-requests/${rid}/decide`, { method: 'POST', body: JSON.stringify({ action }) });
      toast.success(action === 'approve' ? 'Approved — worker assigned' : 'Request denied'); loadPartners();
    } catch (e) { toast.error(e.message); }
  };

  const createPool = async () => {
    if (!newPool.name.trim()) { toast.error('Pool name required'); return; }
    setBusy('pool');
    try {
      await apiCall('/api/org/float-pools', { method: 'POST', body: JSON.stringify(newPool) });
      toast.success('Float pool created'); setNewPool({ name: '', type: 'employer' }); loadPools();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const addPoolWorker = async (pid, workerId) => {
    if (!workerId) return;
    try {
      await apiCall(`/api/org/float-pools/${pid}/workers`, { method: 'POST', body: JSON.stringify({ worker_id: workerId }) });
      toast.success('Worker added to pool'); loadPools();
    } catch (e) { toast.error(e.message); }
  };

  const removePoolWorker = async (pid, workerId) => {
    try {
      await apiCall(`/api/org/float-pools/${pid}/workers/${workerId}`, { method: 'DELETE' });
      loadPools();
    } catch (e) { toast.error(e.message); }
  };

  const deletePool = async (pid) => {
    try {
      await apiCall(`/api/org/float-pools/${pid}`, { method: 'DELETE' });
      toast.success('Pool deleted'); loadPools();
    } catch (e) { toast.error(e.message); }
  };

  const runDiscover = async () => {
    setBusy('discover');
    try {
      const r = await apiCall('/api/org/partners/discover');
      setDiscover(r.organizations || []);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  if (settings === null) return <SkeletonRows n={5} />;
  const incoming = requests.filter((r) => r.direction === 'incoming' && r.status === 'pending');
  return (
    <div className="space-y-5" data-testid="sharing-pools-tab">
      {/* Sharing settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Worker Sharing Participation</p>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="accent-indigo-600 w-4 h-4" data-testid="sharing-enabled-toggle" />
            Participate in cross-organization worker sharing
          </label>
          <input value={settings.region || ''} onChange={(e) => setSettings({ ...settings, region: e.target.value })}
            placeholder="Region (e.g. Bay Area)" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44"
            data-testid="sharing-region-input" />
          <button onClick={saveSettings} disabled={busy === 'settings'}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
            data-testid="sharing-save-btn">Save</button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">OFF by default — no worker is visible to another organization until you enable sharing, form a partnership, and place workers in a Shared pool. Assignments always require your approval.</p>
      </div>

      {/* Incoming sharing requests */}
      {incoming.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="incoming-sharing-requests">
          <p className="text-xs font-bold text-amber-700 uppercase mb-2">Shared-worker requests awaiting your approval</p>
          {incoming.map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 py-1.5">
              <p className="text-sm text-slate-700 min-w-0 truncate">{r.requester_name} requests <span className="font-semibold">{r.worker_name}</span> for &apos;{r.job_title}&apos;</p>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => decide(r._id, 'approve')} className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold" data-testid={`sharing-approve-${r._id}`}>Approve</button>
                <button onClick={() => decide(r._id, 'deny')} className="px-2.5 py-1 rounded-lg bg-red-100 text-red-600 text-xs font-semibold" data-testid={`sharing-deny-${r._id}`}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Partners */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-500 uppercase">Partner Organizations</p>
          <button onClick={runDiscover} disabled={busy === 'discover'} className="text-xs text-indigo-600 inline-flex items-center gap-1" data-testid="partners-discover-btn">
            <Search className="w-3.5 h-3.5" /> Discover
          </button>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Partner org email"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm flex-1 max-w-xs" data-testid="partner-invite-email" />
          <button onClick={() => invite({ email: inviteEmail })} disabled={busy === 'invite' || !inviteEmail.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="partner-invite-btn"><Send className="w-3.5 h-3.5" /> Invite</button>
        </div>
        {discover && (
          <div className="mb-3 border border-slate-200 rounded-lg p-2 space-y-1" data-testid="partners-discover-list">
            {!discover.length && <p className="text-xs text-slate-400 py-1 px-1">No other organizations with sharing enabled.</p>}
            {discover.map((o) => (
              <div key={o.org_id} className="flex items-center justify-between px-1 py-1">
                <p className="text-xs text-slate-600">{o.name} <span className="text-slate-400">({o.role}{o.region ? ` · ${o.region}` : ''})</span></p>
                <button onClick={() => invite({ org_id: o.org_id })} className="text-xs text-indigo-600 font-semibold" data-testid={`discover-invite-${o.org_id}`}>Invite</button>
              </div>
            ))}
          </div>
        )}
        {!partners.length ? <p className="text-xs text-slate-400">No partnerships yet.</p> : (
          <ul className="divide-y divide-slate-100">
            {partners.map((p) => (
              <li key={p._id} className="flex items-center justify-between gap-2 py-2" data-testid={`partner-row-${p._id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{p.partner_name || 'Organization'}
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${TIER_STYLES[p.tier]}`}>{p.tier}</span>
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{p.status}</span>
                  </p>
                  <p className="text-[11px] text-slate-400">{p.direction}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {p.status === 'pending' && p.direction === 'incoming' && (
                    <>
                      <button onClick={() => respond(p._id, 'accept')} className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold" data-testid={`partner-accept-${p._id}`}>Accept</button>
                      <button onClick={() => respond(p._id, 'decline')} className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs" data-testid={`partner-decline-${p._id}`}>Decline</button>
                    </>
                  )}
                  <button onClick={() => endPartner(p._id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="End partnership" data-testid={`partner-end-${p._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Float pools */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Float Pools</p>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input value={newPool.name} onChange={(e) => setNewPool({ ...newPool, name: e.target.value })}
            placeholder="Pool name" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44" data-testid="pool-name-input" />
          <select value={newPool.type} onChange={(e) => setNewPool({ ...newPool, type: e.target.value })}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="pool-type-select">
            <option value="employer">Employer</option>
            <option value="agency">Agency</option>
            <option value="emergency">Emergency</option>
            <option value="shared">Shared (visible to partners)</option>
            <option value="regional">Regional</option>
          </select>
          <button onClick={createPool} disabled={busy === 'pool'}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="pool-create-btn"><Plus className="w-3.5 h-3.5" /> Create pool</button>
        </div>
        {!pools.length ? <p className="text-xs text-slate-400">No float pools yet — create one to keep standby workers ready for coverage gaps.</p> : (
          <div className="space-y-3">
            {pools.map((p) => (
              <div key={p._id} className="border border-slate-200 rounded-xl p-3" data-testid={`pool-card-${p._id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-indigo-500" /> {p.name}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.type === 'shared' ? 'bg-violet-100 text-violet-700' : p.type === 'emergency' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>{p.type}</span>
                  </p>
                  <button onClick={() => deletePool(p._id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`pool-delete-${p._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {p.workers.map((w) => (
                    <span key={w.worker_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs text-slate-700" data-testid={`pool-worker-${p._id}-${w.worker_id}`}>
                      {w.name}
                      <button onClick={() => removePoolWorker(p._id, w.worker_id)} className="text-slate-400 hover:text-red-500" data-testid={`pool-remove-worker-${p._id}-${w.worker_id}`}><XCircle className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {!p.workers.length && <span className="text-xs text-slate-400">No workers in this pool.</span>}
                </div>
                <select defaultValue="" onChange={(e) => { addPoolWorker(p._id, e.target.value); e.target.value = ''; }}
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs" data-testid={`pool-add-worker-${p._id}`}>
                  <option value="" disabled>+ Add roster worker…</option>
                  {roster.filter((w) => !p.workers.some((pw) => pw.worker_id === w.id)).map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
